import React, { useEffect, useState } from 'react';
import ContractReportTemplate from '../../_components/templates/ContractReportTemplate';
import TabSelection from '../../_components/molecules/TabSelection';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  CourtHammer,
  Exclamation,
  ProgressComparison,
  RegulationsCard,
  RegulationsStatusCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useLocation, useNavigate } from 'react-router-dom';
import GifWithLabels from '@HomaHealth/ui-component-library/lib/_patient-access-components/molecules/GIFWithLabels';
import Contract from '../../../public/Images/Contract.png';
import contractAIQConstants from '../../core-utils/messages/ContractAIQConstants';
import ClauseInfo from '../../_components/organisms/ClauseInfo';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { useStores } from '../../stores';
import {
  TitleAndSubTitle,
  getSOBReportBreadcrumbList,
} from '../../core-utils/helper-utils/contractAIQHelper';
import Loader from '../../_components/molecules/Loader';
import { FusionCharts } from '@HomaHealth/ui-component-library';
import RegulationUnMetText from '../../_components/molecules/RegulationUnMetText';
import Divider from '../../_components/atoms/Divider';
import AllDone from '../../../public/Images/AllDone.png';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import { handleDownloadReport } from '../../core-utils/helper-utils/contractReportHelper';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import Snackbar from '../../_components/atoms/Snackbar';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { PDF_WORKER_URL } from '../../core-utils/helper-utils/pdfWorker';

const RegulatoryComplianceReport = () => {
  //styles
  const useStyles = makeStyles()({
    tabContent: {
      paddingTop: '24px',
    },
    overviewTab: {
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
    },
    cards: { display: 'flex', gap: '16px' },
    notMetRegCardBody: {
      height: '303px',
      overflow: 'auto',
      ...scrollBarStyles,
    },
    legendsGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    legend: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
  });

  //constants
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const sobBreadcrumbs = getSOBReportBreadcrumbList(navigate, location?.state);
  const { reportName, planDocId }: any = location.state;
  const constants =
    contractAIQConstants.CONTRACT_REPORTS.REGULATORY_COMPLIANCE_REPORT;
  const employerGroupStore = useStores().employerGroupStore;
  const contractStore = useStores().contractAiqStore;

  //states
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<{
    id: string;
    view: string;
    question?: string;
  }>({
    id: 'summary',
    view: 'summary',
  });
  const [regulations, setRegulations] = useState<any>([]);
  const [selectedRegulation, setSelectedRegulation] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [loader] = useState<any>(<Loader />);
  // Create a ref for the div that you want to scroll into view
  const divRef = React.useRef<any>(null);
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const [binary, setBinary] = useState<any>();
  const [docLoading, setDocLoading] = useState<boolean>(true);

  //APIs
  const getRegulatoryComplianceReportData = () => {
    const url =
      properties.aiqBaseUrl +
      properties.contractBaseUrl +
      properties.regulatoryComplianceReport;
    axios
      .post(url, {
        eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
        spd_agreement_id: contractStore.spdAgreementId,
        report_name: reportName,
      })
      .then((response) => {
        const data = response?.data?.data?.regulations;
        setRegulations(data);
        setLoading(false);
      })
      .catch((error) => {
        throw error;
      });
  };

  //functions
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleReportSummary = () => {
    setActiveTab({ id: 'summary', view: 'summary' });
  };

  const handleDownloadRegulatoryReport = () => {
    handleDownloadReport(
      'regulatoryReport',
      employerGroupStore?.employerGroupResponseData?.employer_group_name[0],
      planDocId,
      handleOpenSnackBar,
      employerGroupStore?.employerGroupResponseData?.eg_nid[0],
      '',
      '',
    );
  };

  const getPdf = async () => {
    const token = JSON.parse(localStorage.getItem('token-storage') as string)
      ?.access_token;

    await axios
      .post(
        properties.aiqBaseUrl + properties.downloadComplianceAssuranceReport,
        {
          contract_id: planDocId,
          employer:
            employerGroupStore.employerGroupResponseData.employer_group_name[0],
          reportName: 'regulatoryReport',
          compare_agreement_id: '',
          eg_nid: employerGroupStore.employerGroupResponseData.eg_nid[0],
        },
        {
          headers: {
            Authorization: token,
          },
        },
      )
      .then((res) => {
        if (res) {
          const base64Data = res.data?.data;
          // setBinary(base64Data);
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
          const byteArray = new Uint8Array(byteNumbers);
          setBinary(byteArray);
          setDocLoading(false);
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 5000);
      });
  };

  //function to get content of report summary tabs based on tab index
  const getTabContent = () => {
    switch (selectedTabIndex) {
      case 0:
        return <OverviewTab />;
      case 1:
        return <RegulationsTab />;
      default:
        return <></>;
    }
  };

  //function to get content on the right based on left tab selection
   
  const getActiveTabContent = () => {
    switch (activeTab.view) {
      case 'summary':
        return <ReportSummaryContent />;
      case 'selected-regulation':
        return <SelectedRegulation />;
      case 'not-applicable':
        return <NotApplicableRegulation />;
      default:
        return <></>;
    }
  };

  //useEffects
  useEffect(() => {
    getRegulatoryComplianceReportData();
  }, []);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeTab.question, divRef]);

  useEffect(() => {
    getPdf();
  }, []);

  //components
  //component for list of Regulations on the left
  const Regulations = () => {
    return (
      <Grid>
        {regulations?.map((item: any) => {
          const totalCount =
            item?.metList?.length +
            item?.notMetList?.length +
            item?.inSufficientDataList?.length;
          const metCount = item?.metList?.length;
          // const notMetCount = item?.notMetList?.length;
          const notSufficientDataCount = item?.inSufficientDataList?.length;

          const type = metCount === totalCount ? 'Met' : 'Insufficient';
          const description =
            type === 'Insufficient'
              ? `${notSufficientDataCount} Regulation${
                  notSufficientDataCount !== 1 ? 's' : ''
                } with Insufficient Data`
              : 'All Regulations Met';

          return (
            <RegulationsCard
              type={type}
              title={item?.regulation}
              description={description}
              count=""
              handleOnclick={() => {
                setActiveTab({
                  id: item?.regulation,
                  view: 'selected-regulation',
                });
                setSelectedRegulation(item);
              }}
              isActive={activeTab.id === item?.regulation}
            />
          );
        })}
      </Grid>
    );
  };

  //component for report summary content
  const ReportSummaryContent = () => {
    return (
      <>
        <TabSelection
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          tabList={constants.SUMMARY_TABS}
        />
        <Grid className={classes.tabContent}>{getTabContent()}</Grid>
      </>
    );
  };

  //components for repetitive Typography
  const getHeadingTypography = (label: string, margin?: string) => {
    return (
      <Typography
        fontSize="16px"
        fontType="semiBold"
        lineHeight="140%"
        textColor={patientAccessTheme.palette.text.highemphasis}
        margin={margin}
      >
        {label}
      </Typography>
    );
  };

  const getSubHeadingTypography = (label: string, bold?: boolean) => {
    return (
      <Typography
        fontSize="14px"
        fontType={bold ? 'semiBold' : 'regular'}
        lineHeight="24px"
        textColor={patientAccessTheme.palette.text.mediumemphasis}
        display="inline"
      >
        {label}
      </Typography>
    );
  };

  const getCardHeadingTypography = (label: string, margin?: string) => {
    return (
      <Typography
        fontSize="16px"
        fontType={'semiBold'}
        lineHeight="22.4px"
        textColor={patientAccessTheme.palette.text.mediumemphasis}
        margin={margin}
      >
        {label}
      </Typography>
    );
  };

  //component for legends
  const getLegends = (data: any) => {
    return (
      <Grid className={classes.legendsGrid}>
        {data?.map((item: any) => {
          return (
            <>
              {item?.value !== 0 && (
                <Grid className={classes.legend}>
                  <Grid
                    style={{
                      backgroundColor: item?.color,
                      height: '10px',
                      width: '10px',
                      borderRadius: '2px',
                    }}
                  />
                  <Typography
                    fontSize="12px"
                    lineHeight="16.8px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.mediumemphasis}
                  >
                    {item.label}:
                  </Typography>
                  <Typography
                    fontSize="12px"
                    lineHeight="16.8px"
                    fontType="medium"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    {item.value}
                  </Typography>
                </Grid>
              )}
            </>
          );
        })}
      </Grid>
    );
  };

  //component for overview tab content
  const OverviewTab = () => {
    let totalMetListCount = 0;
    let totalNotMetListCount = 0;
    let totalInSuffDataListCount = 0;
    const regulationsFullyMet: any[] = [];
    const regulationsFullyNotMet: any[] = [];

    regulations?.forEach(
      ({ regulation, metList, notMetList, inSufficientDataList }: any) => {
        const totalCount =
          metList?.length + notMetList?.length + inSufficientDataList?.length;
        const metCount = metList?.length;
        const notMetCount = notMetList?.length;
        const inSuffcientDataCount = inSufficientDataList?.length;

        totalMetListCount += metCount;
        totalNotMetListCount += notMetCount;
        totalInSuffDataListCount += inSuffcientDataCount;
        if (totalCount === metCount) {
          regulationsFullyMet?.push({ regulation });
        } else {
          regulationsFullyNotMet?.push({
            regulation,
            count: `${metCount} / ${totalCount}`,
          });
        }
      },
    );

    const totalCount =
      totalMetListCount + totalInSuffDataListCount + totalNotMetListCount;

    const datasource = {
      chart: {
        theme: 'fusion',
        bgColor: patientAccessTheme.palette.status.white,
        showLegend: '0',
        showBorder: '0',
        showLabels: '0',
        showValues: '0',
        showToolTip: '0',
        use3DLighting: '0',
        doughnutRadius: '50%',
        interactiveLegend: '0',
        showPlotBorder: '1',
        plotBorderThickness: '5',
        showShadow: '0',
        plotBorderColor: patientAccessTheme.palette.status.white,
        showZeroPies: 1,
        enableSlicing: 0,
        chartBottomMargin: 10,
        chartTopMargin: -20,
        chartRightMargin: 35,
      },
      data: [
        {
          label: 'Met',
          value: totalMetListCount,
          color: '#17a39c',
        },
        {
          label: 'Not Met',
          value: totalNotMetListCount,
          color: '#d6545e',
        },
        {
          label: 'Insufficient Data',
          value: totalInSuffDataListCount,
          color: '#E1A948',
        },
      ],
    };

    return (
      <Grid className={classes.overviewTab}>
        <Grid>
          {getHeadingTypography(constants.SUMMARY.TITLE, '0px 0px 12px 0px')}
          <Grid>
            {getSubHeadingTypography(
              constants.SUMMARY.SUBTITLE.PART1 +
                `${
                  regulationsFullyMet?.length === 0
                    ? 'no'
                    : regulationsFullyNotMet?.length === 0
                    ? 'all'
                    : 'some'
                } regulations`,
            )}
            {regulationsFullyMet?.length !== 0 &&
              regulationsFullyMet?.map((item: any, index: number) => (
                <>
                  {getSubHeadingTypography(
                    `(${item?.regulation})${
                      index !== regulationsFullyMet?.length - 1 ? ', ' : ''
                    }`,
                    true,
                  )}
                </>
              ))}
            {regulationsFullyNotMet?.length !== 0 && (
              <>
                {getSubHeadingTypography(constants.SUMMARY.SUBTITLE.PART2)}
                {regulationsFullyNotMet?.map((item: any, index: number) => (
                  <>
                    {getSubHeadingTypography(
                      `${item?.regulation} (${item?.count} met)${
                        index === regulationsFullyNotMet?.length - 1
                          ? '.'
                          : index === regulationsFullyNotMet?.length - 2
                          ? ' '
                          : ', '
                      }`,
                      true,
                    )}
                    {index === regulationsFullyNotMet?.length - 2 &&
                      getSubHeadingTypography(' and ')}
                  </>
                ))}
                {getSubHeadingTypography(constants.SUMMARY.SUBTITLE.PART3)}
              </>
            )}
          </Grid>
        </Grid>
        <Grid>
          {getHeadingTypography(constants.INSIGHTS.TITLE, '0px 0px 16px 0px')}
          <Grid className={classes.cards}>
            <CardComponent
              padding="16px"
              width="290px"
              height="338px"
              title={getCardHeadingTypography(
                constants.INSIGHTS.TOTAL_REGULATIONS,
              )}
              titleSubTitleMargin="8px"
              subTitle={
                <Typography
                  fontSize="24px"
                  fontType="medium"
                  lineHeight="140%"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {totalCount}
                </Typography>
              }
              body={
                <>
                  <FusionCharts
                    typeOfChart={'doughnut2d'}
                    widthOfChart={'280'}
                    heightOfChart={'180'}
                    dataSource={datasource}
                  />
                  {getLegends(datasource?.data)}
                </>
              }
            />
            <CardComponent
              padding="16px"
              width="650px"
              height="338px"
              title={''}
              body={
                totalInSuffDataListCount !== 0 ? (
                  <Grid className={classes.notMetRegCardBody}>
                    {getCardHeadingTypography(
                      'Total Regulations with Insufficient Data',
                      '0px 0px 16px 0px',
                    )}
                    {regulations?.map((regulation: any) => {
                      return regulation?.inSufficientDataList?.map(
                        (item: any, index: number) => (
                          <Grid
                            style={{
                              marginBottom:
                                index ===
                                regulation?.inSufficientDataList?.length - 1
                                  ? '0px'
                                  : '12px',
                            }}
                          >
                            <RegulationUnMetText
                              underlinedText={item?.question_title}
                              bracketsText={regulation?.regulation}
                              handleOnclick={() => {
                                setActiveTab({
                                  id: regulation?.regulation,
                                  view: 'selected-regulation',
                                  question: item?.question_title,
                                });
                                setSelectedRegulation(regulation);
                              }}
                              icon={<Exclamation />}
                            />
                          </Grid>
                        ),
                      );
                    })}
                  </Grid>
                ) : (
                  <GifWithLabels
                    mainLabel={constants.ALL_MET.TITLE}
                    subLabel={constants.ALL_MET.SUBTITLE}
                    gifUrl={AllDone}
                  />
                )
              }
            />
          </Grid>
        </Grid>
        <Grid
          className={classes.cards}
          sx={{
            flexDirection: 'column',
          }}
        >
          {getHeadingTypography(constants.ABOUT)}
          {regulations?.map((regulation: any) => (
            <CardComponent
              padding="16px"
              width="100%"
              title={<CourtHammer />}
              subTitle={
                <Typography
                  fontSize="14px"
                  fontType="medium"
                  lineHeight="24px"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {regulation?.regulation}
                </Typography>
              }
              body={
                <>
                  {getSubHeadingTypography(regulation?.regulation_description)}
                </>
              }
            />
          ))}
        </Grid>
      </Grid>
    );
  };

  //component for regulations tab content
  const RegulationsTab = () => {
    return (
      <>
        {regulations?.map((regulation: any, index: number) => (
          <Grid
            style={{
              marginBottom: index === regulations?.length - 1 ? '0px' : '32px',
            }}
          >
            <Typography
              fontSize="18px"
              fontType="semiBold"
              lineHeight="21.78px"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {regulation?.regulation}
            </Typography>
            <Divider marginTop={'16px'} marginBottom={'24px'} />
            {ListOfRegulationCategories(regulation)}
          </Grid>
        ))}
      </>
    );
  };

  //component for list of categories for a regulation
  const ListOfRegulationCategories = (regulation: any) => {
    return (
      <Grid>
        {regulation?.notMetList?.length > 0 && (
          <>
            {regulation?.notMetList?.map((item: any) => (
              <Grid
                style={{ marginBottom: '12px' }}
                ref={
                  item?.question_title === activeTab?.question ? divRef : null
                }
              >
                <ClauseInfo
                  type={'Not Met'}
                  title={item?.question_title}
                  summaryTitle={'Question'}
                  summaryDescription={item?.question}
                  analysisTitle="Summary of Provision"
                  analysisDescription={item?.summary}
                  reasonsTitle="Why this provision is not met?"
                  reasons={[item?.reason]}
                  clausesTitle={
                    item?.exact_text !== 'NaN' ? 'Clause in Contract' : ''
                  }
                  clauses={[item?.exact_text]}
                  isHeaderCard
                  expandDefault={activeTab?.question === item?.question_title}
                />
              </Grid>
            ))}
          </>
        )}
        {regulation?.inSufficientDataList?.length > 0 && (
          <>
            {regulation?.inSufficientDataList?.map((item: any) => (
              <Grid
                style={{ marginBottom: '12px' }}
                ref={
                  item?.question_title === activeTab?.question ? divRef : null
                }
              >
                <ClauseInfo
                  type={'InSufficient'}
                  title={item?.question_title}
                  summaryTitle={'Question'}
                  summaryDescription={item?.question}
                  analysisTitle="Summary of Provision"
                  analysisDescription={item?.summary}
                  reasonsTitle="Analysis of Insufficient data from the document"
                  reasons={[item?.reason]}
                  isHeaderCard
                  expandDefault={activeTab?.question === item?.question_title}
                />
              </Grid>
            ))}
          </>
        )}
        {regulation?.metList?.length > 0 && (
          <>
            {regulation?.metList?.map((item: any) => (
              <Grid style={{ marginBottom: '12px' }}>
                <ClauseInfo
                  type={'Met'}
                  title={item?.question_title}
                  content={
                    <GifWithLabels
                      mainLabel={'Regulation Met'}
                      subLabel={''}
                      gifUrl={Contract}
                      gifWidth="150px"
                      gifHeight="150px"
                    />
                  }
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>
    );
  };

  //component for selected regulation
  const SelectedRegulation = () => {
    const totalCount =
      selectedRegulation?.metList?.length +
      selectedRegulation?.notMetList?.length +
      selectedRegulation?.inSufficientDataList?.length;
    const metCount = selectedRegulation?.metList?.length;
    const notMetCount = selectedRegulation?.notMetList?.length;
    const insufficientDataCount =
      selectedRegulation?.inSufficientDataList?.length;
    const progress = (metCount / totalCount) * 100;

    const categories = [
      {
        name: 'Regulation Met',
        color: '#17A39C',
        value: metCount,
      },
      {
        name: ' Not Sufficient Data',
        color: '#E1A948',
        value: insufficientDataCount,
      },
      {
        name: 'Not Met',
        color: '#D6545E',
        value: notMetCount,
      },
    ];

    return (
      <>
        {TitleAndSubTitle(
          selectedRegulation?.regulation,
          selectedRegulation?.regulation_description,
        )}
        <Divider marginBottom="24px" marginTop="24px" />
        <Grid>
          {notMetCount !== 0 || insufficientDataCount !== 0 ? (
            <CardComponent
              title={'Regulations'}
              body={<ProgressComparison categories={categories} />}
            />
          ) : (
            <RegulationsStatusCard
              type={'Met'}
              width="100%"
              title={constants.INSIGHTS.REGULATIONS_MET}
              count={`${metCount}/${totalCount}`}
              progressValue={progress}
            />
          )}
        </Grid>
        <Divider marginBottom="24px" marginTop="48px" />
        <Typography
          fontSize="14px"
          fontType="medium"
          lineHeight="22.4px"
          textColor={patientAccessTheme.palette.text.highemphasis}
          margin="0px 0px 24px 0px"
        >
          {constants.REGULATIONS}
        </Typography>
        {ListOfRegulationCategories(selectedRegulation)}
      </>
    );
  };

  //component for not applicable regulations
  const NotApplicableRegulation = () => {
    return <></>;
  };

  return (
    <>
      {loading ? (
        loader
      ) : (
        <ContractReportTemplate
          breadcrumbsList={sobBreadcrumbs}
          reportName={`${reportName} `}
          document={''}
          handleDocumentOnclick={() => {}}
          handleDownloadReport={handleDownloadRegulatoryReport}
          showDownloadReport={true}
          handleReportSummary={handleReportSummary}
          isReportSummaryActive={activeTab.id === 'summary'}
          leftContent={false && <Regulations />}
          // rightContent={getActiveTabContent()}
          rightContent={
            docLoading
              ? loader
              : binary && (
                  <Worker workerUrl={PDF_WORKER_URL}>
                    <Grid container style={{ marginBottom: '29px' }}>
                      <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        fontType="semiBold"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                        margin="0px 0px 20px 0px"
                      >
                        {'Report PDF'}
                      </Typography>
                      <Viewer fileUrl={binary} />
                    </Grid>
                  </Worker>
                )
          }
        />
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};

export default RegulatoryComplianceReport;
